import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { improvementsEndpoints as endpoints } from '@/api/endpoints';

import { API } from './useImprovements';
import type {
  Improvement,
  ImprovementsListResponse,
  ReviewStatus,
  UpdateImprovementBody,
} from '../types';

const SUCCESS_TOAST_KEYS: Record<ReviewStatus, string> = {
  approved: 'improvements.applied_toast',
  rejected: 'improvements.dismissed_toast',
  resolved: 'improvements.archived_toast',
};

type UpdateVariables = { id: string; body: UpdateImprovementBody };
type ListSnapshot = InfiniteData<ImprovementsListResponse> | undefined;

/**
 * PATCHes an improvement's review status/feedback with an optimistic cache
 * update (the row flips status immediately) and rollback on failure.
 */
export const useUpdateImprovement = (brainId?: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const listKey = [endpoints.improvements(brainId!)];

  return useMutation<Improvement, Error, UpdateVariables, { previous: ListSnapshot }>({
    mutationFn: ({ id, body }) => API.updateImprovement(brainId!, id, body),
    onMutate: async ({ id, body }) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<ListSnapshot>(listKey);

      queryClient.setQueryData<ListSnapshot>(
        listKey,
        (prev) =>
          prev && {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              improvements: page.improvements.map((improvement) =>
                improvement.improvement_id === id
                  ? {
                      ...improvement,
                      status: body.status,
                      ...(body.feedback !== undefined ? { feedback: body.feedback } : {}),
                    }
                  : improvement
              ),
            })),
          }
      );

      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context) {
        queryClient.setQueryData(listKey, context.previous);
      }
      toast.error(error.message);
    },
    onSuccess: (_resp, { body }) => {
      toast.success(t(SUCCESS_TOAST_KEYS[body.status]));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
};
